import { clamp } from "./utils";

export type RoutePoint = {
  index: number;
  lat: number;
  lon: number;
  ele: number | null;
  time: string | null;
  distance: number;
  sourceSegment: number;
  trkpt: Element;
};

export type Waypoint = {
  index: number;
  lat: number;
  lon: number;
  ele: number | null;
  time: string | null;
  name: string | null;
  desc: string | null;
  nearestPointIndex: number;
  wpt: Element;
};

export type RouteData = {
  name: string;
  fileName: string;
  document: Document;
  points: RoutePoint[];
  waypoints: Waypoint[];
  totalDistance: number;
  ascent: number;
  descent: number;
  bounds: [[number, number], [number, number]];
};

export type Segment = {
  id: number;
  start: number;
  end: number;
  name: string;
  startDistance: number;
  endDistance: number;
  distance: number;
  ascent: number;
  descent: number;
  minEle: number | null;
  maxEle: number | null;
  slopeDistances: SlopeDistance[];
};

export type SpeedModelSettings = {
  powerWatts: number;
  massKg: number;
  cda: number;
  crr: number;
};

export type SpeedEstimate = {
  movingTimeSeconds: number;
  averageSpeedMps: number;
};

export type SlopeDistance = {
  name: string;
  label: string;
  color: string;
  distance: number;
};

const GPX_NAMESPACE = "http://www.topografix.com/GPX/1/1";
const GRAVITY = 9.80665;
const ROAD_BIKE = {
  drivetrainEfficiency: 0.97,
  airDensity: 1.225,
  gradeCutoff: 0.015,
  elevationBufferM: 10,
  minSpeedMps: 1.2,
  maxDescentSpeedMps: 40 / 3.6,
};

export const DEFAULT_SPEED_MODEL_SETTINGS: SpeedModelSettings = {
  powerWatts: 105,
  massKg: 95,
  cda: 0.38,
  crr: 0.0065,
};
export const SPEED_MODEL_LIMITS = {
  powerWatts: { min: 30, max: 600 },
  massKg: { min: 40, max: 180 },
  cda: { min: 0.18, max: 0.7 },
  crr: { min: 0.003, max: 0.03 },
} as const;

export function parseGpx(text: string, fileName: string): RouteData {
  const document = parseGpxDocument(text);

  const sourceSegments = Array.from(document.querySelectorAll("trkseg")).map((trkseg, sourceSegment) => ({
    sourceSegment,
    trkpts: Array.from(trkseg.querySelectorAll("trkpt")),
  }));
  const trkpts = sourceSegments.flatMap(segment => segment.trkpts);
  if (trkpts.length < 2) throw new Error("GPX file must contain at least two track points.");

  let distance = 0;
  const points: RoutePoint[] = [];
  const bounds: [[number, number], [number, number]] = [
    [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY],
    [Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY],
  ];

  for (const { sourceSegment, trkpts } of sourceSegments) {
    for (const trkpt of trkpts) {
      const lat = Number(trkpt.getAttribute("lat"));
      const lon = Number(trkpt.getAttribute("lon"));
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

      const eleText = trkpt.querySelector("ele")?.textContent;
      const ele = eleText === undefined || eleText === null || eleText === "" ? null : Number(eleText);
      const cleanEle = ele !== null && Number.isFinite(ele) ? ele : null;
      const previous = points.at(-1);
      if (previous) distance += haversine(previous.lat, previous.lon, lat, lon);

      extendBounds(bounds, lon, lat);

      points.push({
        index: points.length,
        lat,
        lon,
        ele: cleanEle,
        time: trkpt.querySelector("time")?.textContent ?? null,
        distance,
        sourceSegment,
        trkpt,
      });
    }
  }

  if (points.length < 2) throw new Error("GPX file has no usable track points.");
  const elevation = calculateElevationChangeBySourceSegment(points);
  const waypoints = parseWaypoints(document, points);
  for (const waypoint of waypoints) extendBounds(bounds, waypoint.lon, waypoint.lat);

  return {
    name: routeName(document) || baseName(fileName),
    fileName,
    document,
    points,
    waypoints,
    totalDistance: distance,
    ascent: elevation.ascent,
    descent: elevation.descent,
    bounds,
  };
}

export function buildSegments(points: RoutePoint[], splitIndexes: number[]): Segment[] {
  const cuts = [0, ...splitIndexes.filter(index => index > 0 && index < points.length - 1).sort((a, b) => a - b), points.length - 1];

  return cuts.slice(0, -1).map((start, id) => {
    const end = cuts[id + 1]!;
    const slice = points.slice(start, end + 1);
    const { ascent, descent } = calculateElevationChangeBySourceSegment(slice);
    let minEle: number | null = null;
    let maxEle: number | null = null;

    for (let index = 0; index < slice.length; index++) {
      const point = slice[index]!;
      if (point.ele !== null) {
        minEle = minEle === null ? point.ele : Math.min(minEle, point.ele);
        maxEle = maxEle === null ? point.ele : Math.max(maxEle, point.ele);
      }
    }

    return {
      id: id + 1,
      start,
      end,
      name: `Segment ${id + 1}`,
      startDistance: points[start]!.distance,
      endDistance: points[end]!.distance,
      distance: points[end]!.distance - points[start]!.distance,
      ascent,
      descent,
      minEle,
      maxEle,
      slopeDistances: calculateSlopeDistances(slice),
    };
  });
}

export function calculateRouteSpeed(points: Pick<RoutePoint, "distance" | "ele" | "sourceSegment">[], rawSettings: SpeedModelSettings): SpeedEstimate {
  if (points.length < 2) return { movingTimeSeconds: 0, averageSpeedMps: 0 };
  return calculateSpeedEstimate(points, sanitizeSpeedSettings(rawSettings), 0, points.length - 1);
}

export function calculateCumulativeSpeedTimes(points: Pick<RoutePoint, "distance" | "ele" | "sourceSegment">[], rawSettings: SpeedModelSettings, startIndex = 0, endIndex = points.length - 1): number[] {
  const times = Array(points.length).fill(0);
  const settings = sanitizeSpeedSettings(rawSettings);
  let movingTimeSeconds = 0;
  let previousSpeed = Math.min(ROAD_BIKE.maxDescentSpeedMps, 7);
  let elevationBuffer = 0;

  for (let index = startIndex; index < endIndex; index++) {
    const start = points[index]!;
    const end = points[index + 1]!;
    const distance = end.distance - start.distance;
    if (distance > 0 && start.sourceSegment === end.sourceSegment) {
      const rawElevation = (end.ele ?? start.ele ?? 0) - (start.ele ?? end.ele ?? 0);
      const effectiveElevation = bufferedElevationDelta(rawElevation, distance, elevationBuffer);
      elevationBuffer = effectiveElevation.buffer;
      const speed = segmentSpeed(distance, effectiveElevation.delta, settings, previousSpeed);
      movingTimeSeconds += distance / speed;
      previousSpeed = speed;
    }
    times[index + 1] = movingTimeSeconds;
  }

  return times;
}

function calculateSpeedEstimate(points: Pick<RoutePoint, "distance" | "ele" | "sourceSegment">[], settings: SpeedModelSettings, startIndex: number, endIndex: number): SpeedEstimate {
  let movingTimeSeconds = 0;
  let movingDistance = 0;
  let previousSpeed = Math.min(ROAD_BIKE.maxDescentSpeedMps, 7);
  let elevationBuffer = 0;

  for (let index = startIndex; index < endIndex; index++) {
    const start = points[index]!;
    const end = points[index + 1]!;
    const distance = end.distance - start.distance;
    if (distance <= 0 || start.sourceSegment !== end.sourceSegment) continue;

    const rawElevation = (end.ele ?? start.ele ?? 0) - (start.ele ?? end.ele ?? 0);
    const effectiveElevation = bufferedElevationDelta(rawElevation, distance, elevationBuffer);
    elevationBuffer = effectiveElevation.buffer;
    const speed = segmentSpeed(distance, effectiveElevation.delta, settings, previousSpeed);
    movingDistance += distance;
    movingTimeSeconds += distance / speed;
    previousSpeed = speed;
  }

  return {
    movingTimeSeconds,
    averageSpeedMps: movingTimeSeconds > 0 ? movingDistance / movingTimeSeconds : 0,
  };
}

export function calculateSegmentSpeed(routePoints: RoutePoint[], segment: Pick<Segment, "start" | "end">, settings: SpeedModelSettings): SpeedEstimate {
  return calculateSpeedEstimate(routePoints, sanitizeSpeedSettings(settings), segment.start, segment.end);
}

export function formatMovingTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const minutes = Math.max(1, Math.round(seconds / 60));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return hours > 0 ? `${hours}h ${remainder.toString().padStart(2, "0")}m` : `${minutes}m`;
}

export function formatSpeed(mps: number) {
  return Number.isFinite(mps) && mps > 0 ? `${(mps * 3.6).toFixed(1)} km/h` : "—";
}

export function exportSegmentGpx(route: RouteData, segment: Segment): string {
  const doc = document.implementation.createDocument(GPX_NAMESPACE, "gpx");
  const root = doc.documentElement;
  root.setAttribute("version", route.document.documentElement.getAttribute("version") || "1.1");
  root.setAttribute("creator", "gpxer");

  const trk = doc.createElementNS(GPX_NAMESPACE, "trk");
  const name = doc.createElementNS(GPX_NAMESPACE, "name");
  name.textContent = `${route.name} - ${segment.name}`;
  const trkseg = doc.createElementNS(GPX_NAMESPACE, "trkseg");

  for (const waypoint of route.waypoints) {
    if (isWaypointInSegment(waypoint, segment)) {
      root.appendChild(doc.importNode(waypoint.wpt, true));
    }
  }

  route.points.slice(segment.start, segment.end + 1).forEach(point => {
    trkseg.appendChild(doc.importNode(point.trkpt, true));
  });

  trk.append(name, trkseg);
  root.appendChild(trk);
  return serializeGpxDocument(doc);
}

export function updateGpxRouteName(text: string, name: string): string {
  const cleanName = name.trim();
  if (!cleanName) throw new Error("Route name cannot be empty.");

  const document = parseGpxDocument(text);
  const target = routeNameElement(document) ?? createTrackNameElement(document);
  target.textContent = cleanName;

  return serializeGpxDocument(document);
}

export function downloadText(fileName: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "application/gpx+xml" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

function createTrackNameElement(document: Document) {
  const track = document.querySelector("trk");
  if (!track) throw new Error("GPX file has no track.");
  const name = document.createElementNS(track.namespaceURI || GPX_NAMESPACE, "name");
  track.insertBefore(name, track.firstChild);
  return name;
}

function parseGpxDocument(text: string) {
  const document = new DOMParser().parseFromString(text, "application/xml");
  const error = document.querySelector("parsererror");
  if (error) throw new Error("GPX file is not valid XML.");
  return document;
}

function serializeGpxDocument(document: Document) {
  const xml = new XMLSerializer().serializeToString(document).replace(/^\s*<\?xml[^?]*\?>\s*/i, "");
  return `<?xml version="1.0" encoding="UTF-8"?>\n${xml}\n`;
}

function routeName(document: Document) {
  return routeNameElement(document)?.textContent?.trim() || document.querySelector("name")?.textContent?.trim() || "";
}

function routeNameElement(document: Document) {
  const trackName = directChild(document.querySelector("trk"), "name");
  if (trackName) return trackName;
  return directChild(document.querySelector("metadata"), "name");
}

function directChild(parent: Element | null, tagName: string) {
  if (!parent) return null;
  return Array.from(parent.children).find(child => child.localName === tagName) ?? null;
}

export function nearestPoint(points: RoutePoint[], distanceKm: number) {
  const target = distanceKm * 1000;

  if (points.length === 0) return 0;
  if (target <= points[0]!.distance) return points[0]!.index;
  if (target >= points.at(-1)!.distance) return points.at(-1)!.index;

  let low = 0;
  let high = points.length - 1;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (points[mid]!.distance < target) low = mid + 1;
    else high = mid;
  }

  const before = points[low - 1]!;
  const after = points[low]!;
  return target - before.distance <= after.distance - target ? before.index : after.index;
}

export function formatDistance(meters: number) {
  return `${(meters / 1000).toFixed(1)} km`;
}

export function formatElevation(meters: number | null) {
  return meters === null ? "—" : `${Math.round(meters)} m`;
}

export function safeFileName(value: string) {
  return value.replace(/[^a-z0-9а-яё._-]+/gi, "-").replace(/^-+|-+$/g, "") || "route";
}

export function calculateElevationChange(points: Pick<RoutePoint, "ele" | "distance">[]) {
  if (points.length < 2) return { ascent: 0, descent: 0 };

  const simplified = ramerDouglasPeucker(points, 20);
  let ascent = 0;
  let descent = 0;

  for (let i = 0; i < simplified.length - 1; i++) {
    const start = simplified[i]!;
    const end = simplified[i + 1]!;
    let cumulEle = 0;
    let currentStart = start;
    let currentEnd = start;
    let prevSmoothedEle = 0;

    distanceWindowSmoothing(points, start, end + 1, 100, (s, e) => {
      for (let index = currentStart; index < s; index++) cumulEle -= points[index]?.ele ?? 0;
      for (let index = currentEnd; index <= e; index++) cumulEle += points[index]?.ele ?? 0;
      currentStart = s;
      currentEnd = e + 1;
      return cumulEle / (e - s + 1);
    }, (smoothedEle, index) => {
      if (index === start) {
        smoothedEle = points[start]?.ele ?? 0;
        prevSmoothedEle = smoothedEle;
      } else if (index === end) {
        smoothedEle = points[end]?.ele ?? 0;
      }

      const delta = smoothedEle - prevSmoothedEle;
      if (delta > 0) ascent += delta;
      else if (delta < 0) descent -= delta;
      prevSmoothedEle = smoothedEle;
    });
  }

  return { ascent, descent };
}

export const SLOPE_CLASSES = [
  { maxSlope: -8, name: "steep downhill", label: "< -8%", color: "#15803d" },
  { maxSlope: -4, name: "downhill", label: "-8..-4%", color: "#22c55e" },
  { maxSlope: -1, name: "gentle downhill", label: "-4..-1%", color: "#86efac" },
  { maxSlope: 1, name: "flat", label: "-1..+1%", color: "#d9f99d" },
  { maxSlope: 4, name: "false flat", label: "+1..+4%", color: "#fde047" },
  { maxSlope: 7, name: "climb", label: "+4..+7%", color: "#fb923c" },
  { maxSlope: 10, name: "hard climb", label: "+7..+10%", color: "#ef4444" },
  { maxSlope: Infinity, name: "steep climb", label: "> +10%", color: "#991b1b" },
] as const;

export function getSlopeColor(slope: number) {
  return slopeClassFor(slope).color;
}

export function getSlopeLabel(slope: number) {
  return slopeClassFor(slope).label;
}

export function getSlopeName(slope: number) {
  return slopeClassFor(slope).name;
}

export function calculateSlopeDistances(points: Pick<RoutePoint, "ele" | "distance">[]): SlopeDistance[] {
  const distances = SLOPE_CLASSES.map(slopeClass => ({
    name: slopeClass.name,
    label: slopeClass.label,
    color: slopeClass.color,
    distance: 0,
  }));

  for (const segment of calculateProfileSlopeSegments(points)) {
    const slopeClass = slopeClassFor(segment.slope);
    const index = SLOPE_CLASSES.indexOf(slopeClass);
    distances[index]!.distance += segment.distance;
  }

  return distances;
}

function slopeClassFor(slope: number) {
  for (const slopeClass of SLOPE_CLASSES) {
    if (slope < slopeClass.maxSlope) return slopeClass;
  }
  return SLOPE_CLASSES.at(-1)!;
}

export function calculateProfileSlopes(points: Pick<RoutePoint, "ele" | "distance">[]) {
  return calculateProfileSlopeDetails(points).map(detail => detail.slope);
}

export function calculateProfileSlopeSegments(points: Pick<RoutePoint, "ele" | "distance">[]) {
  if (points.length < 2) return [];

  const simplified = ramerDouglasPeucker(points, 20);
  const segments: { start: number; end: number; slope: number; distance: number }[] = [];

  for (let i = 0; i < simplified.length - 1; i++) {
    const start = simplified[i]!;
    const end = simplified[i + 1]!;
    const distance = points[end]!.distance - points[start]!.distance;
    const elevation = (points[end]!.ele ?? 0) - (points[start]!.ele ?? 0);
    const slope = distance > 0 ? (elevation / distance) * 100 : 0;
    segments.push({ start, end, slope, distance });
  }

  return segments;
}

export function calculateProfileSlopeDetails(points: Pick<RoutePoint, "ele" | "distance">[]) {
  const details = Array.from({ length: points.length }, () => ({ slope: 0, distance: 0 }));

  const segments = calculateProfileSlopeSegments(points);
  for (let i = 0; i < segments.length; i++) {
    const { start, end, slope, distance } = segments[i]!;
    for (let index = start; index < end + (i + 1 === segments.length ? 1 : 0); index++) {
      details[index] = { slope, distance };
    }
  }

  return details;
}

function baseName(fileName: string) {
  return fileName.replace(/\.[^.]+$/, "");
}

function parseWaypoints(document: Document, points: RoutePoint[]): Waypoint[] {
  const waypoints: Waypoint[] = [];
  const wpts = Array.from(document.querySelectorAll("wpt"));

  for (let index = 0; index < wpts.length; index++) {
    const wpt = wpts[index]!;
    const latText = wpt.getAttribute("lat");
    const lonText = wpt.getAttribute("lon");
    if (latText === null || lonText === null) continue;

    const lat = Number(latText);
    const lon = Number(lonText);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

    const eleText = wpt.querySelector("ele")?.textContent;
    const ele = eleText === undefined || eleText === null || eleText === "" ? null : Number(eleText);

    waypoints.push({
      index,
      lat,
      lon,
      ele: ele !== null && Number.isFinite(ele) ? ele : null,
      time: wpt.querySelector("time")?.textContent ?? null,
      name: wpt.querySelector("name")?.textContent?.trim() || null,
      desc: wpt.querySelector("desc")?.textContent?.trim() || null,
      nearestPointIndex: nearestRoutePointIndex(points, lat, lon),
      wpt,
    });
  }

  return waypoints;
}

export function sanitizeSpeedSettings(settings: SpeedModelSettings): SpeedModelSettings {
  return {
    powerWatts: clamp(settings.powerWatts, SPEED_MODEL_LIMITS.powerWatts.min, SPEED_MODEL_LIMITS.powerWatts.max),
    massKg: clamp(settings.massKg, SPEED_MODEL_LIMITS.massKg.min, SPEED_MODEL_LIMITS.massKg.max),
    cda: clamp(settings.cda, SPEED_MODEL_LIMITS.cda.min, SPEED_MODEL_LIMITS.cda.max),
    crr: clamp(settings.crr, SPEED_MODEL_LIMITS.crr.min, SPEED_MODEL_LIMITS.crr.max),
  };
}

function bufferedElevationDelta(rawElevation: number, distance: number, buffer: number) {
  const rawGrade = rawElevation / distance;
  if (Math.abs(rawGrade) <= ROAD_BIKE.gradeCutoff) return { delta: 0, buffer: shrinkBuffer(buffer, Math.abs(rawElevation)) };

  const cutoffElevation = Math.sign(rawElevation) * ROAD_BIKE.gradeCutoff * distance;
  const excessElevation = rawElevation - cutoffElevation;
  const sameDirectionBuffer = Math.sign(buffer) === Math.sign(excessElevation) ? buffer : 0;
  const nextBuffer = sameDirectionBuffer + excessElevation;

  if (Math.abs(nextBuffer) <= ROAD_BIKE.elevationBufferM) return { delta: cutoffElevation, buffer: nextBuffer };

  const overflow = nextBuffer - Math.sign(nextBuffer) * ROAD_BIKE.elevationBufferM;
  return { delta: cutoffElevation + overflow, buffer: Math.sign(nextBuffer) * ROAD_BIKE.elevationBufferM };
}

function shrinkBuffer(buffer: number, amount: number) {
  if (buffer === 0) return 0;
  const next = Math.max(0, Math.abs(buffer) - amount);
  return Math.sign(buffer) * next;
}

function segmentSpeed(distance: number, elevation: number, settings: SpeedModelSettings, previousSpeed: number) {
  const sin = distance > 0 ? elevation / Math.sqrt(distance ** 2 + elevation ** 2) : 0;
  const cos = Math.sqrt(Math.max(0, 1 - sin ** 2));
  const grade = distance > 0 ? elevation / distance : 0;
  let power = settings.powerWatts;
  let gravityMultiplier = 1;

  if (grade <= -0.06) {
    power = 0;
    gravityMultiplier = 0.5;
  } else if (grade <= -0.02) {
    power = 0;
  }

  const speed = solveSteadySpeed({
    powerWatts: power,
    settings,
    previousSpeed,
    sin: sin * gravityMultiplier,
    cos,
  });

  return Math.max(ROAD_BIKE.minSpeedMps, Math.min(ROAD_BIKE.maxDescentSpeedMps, speed));
}

function solveSteadySpeed({
  powerWatts,
  settings,
  previousSpeed,
  sin,
  cos,
}: {
  powerWatts: number;
  settings: SpeedModelSettings;
  previousSpeed: number;
  sin: number;
  cos: number;
}) {
  let speed = Math.max(ROAD_BIKE.minSpeedMps, Math.min(ROAD_BIKE.maxDescentSpeedMps, previousSpeed || 7));
  const aero = 0.5 * ROAD_BIKE.airDensity * settings.cda;
  const rolling = settings.crr * settings.massKg * GRAVITY * cos;
  const gravity = settings.massKg * GRAVITY * sin;
  const deliveredPower = powerWatts * ROAD_BIKE.drivetrainEfficiency;
  const force = rolling + gravity;

  if (deliveredPower === 0 && force < 0) return Math.sqrt(-force / aero);

  for (let iteration = 0; iteration < 16; iteration++) {
    const value = aero * speed ** 3 + force * speed - deliveredPower;
    const derivative = 3 * aero * speed ** 2 + force;
    if (Math.abs(derivative) < 0.001) break;
    const next = speed - value / derivative;
    if (!Number.isFinite(next)) break;
    speed = Math.max(ROAD_BIKE.minSpeedMps, Math.min(ROAD_BIKE.maxDescentSpeedMps, next));
  }

  return speed;
}

function isWaypointInSegment(waypoint: Waypoint, segment: Segment) {
  return waypoint.nearestPointIndex >= segment.start &&
    waypoint.nearestPointIndex <= segment.end &&
    (segment.start === 0 || waypoint.nearestPointIndex > segment.start);
}

function nearestRoutePointIndex(points: RoutePoint[], lat: number, lon: number) {
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const point of points) {
    const distance = haversine(point.lat, point.lon, lat, lon);
    if (distance < bestDistance) {
      bestIndex = point.index;
      bestDistance = distance;
    }
  }

  return bestIndex;
}

function extendBounds(bounds: [[number, number], [number, number]], lon: number, lat: number) {
  bounds[0][0] = Math.min(bounds[0][0], lon);
  bounds[0][1] = Math.min(bounds[0][1], lat);
  bounds[1][0] = Math.max(bounds[1][0], lon);
  bounds[1][1] = Math.max(bounds[1][1], lat);
}

function calculateElevationChangeBySourceSegment(points: RoutePoint[]) {
  let ascent = 0;
  let descent = 0;
  let start = 0;

  while (start < points.length) {
    let end = start + 1;
    while (end < points.length && points[end]!.sourceSegment === points[start]!.sourceSegment) end++;
    const elevation = calculateElevationChange(points.slice(start, end));
    ascent += elevation.ascent;
    descent += elevation.descent;
    start = end;
  }

  return { ascent, descent };
}

function ramerDouglasPeucker(points: Pick<RoutePoint, "ele" | "distance">[], epsilon: number) {
  if (points.length === 0) return [];
  if (points.length === 1) return [0];

  const simplified = [0];
  ramerDouglasPeuckerRecursive(points, epsilon, 0, points.length - 1, simplified);
  simplified.push(points.length - 1);
  return simplified;
}

function ramerDouglasPeuckerRecursive(
  points: Pick<RoutePoint, "ele" | "distance">[],
  epsilon: number,
  start: number,
  end: number,
  simplified: number[],
) {
  let largestIndex = 0;
  let largestDistance = 0;

  for (let index = start + 1; index < end; index++) {
    const distance = elevationProfileDistance(points[start]!, points[end]!, points[index]!);
    if (distance > largestDistance) {
      largestIndex = index;
      largestDistance = distance;
    }
  }

  if (largestDistance > epsilon && largestIndex !== 0) {
    ramerDouglasPeuckerRecursive(points, epsilon, start, largestIndex, simplified);
    simplified.push(largestIndex);
    ramerDouglasPeuckerRecursive(points, epsilon, largestIndex, end, simplified);
  }
}

function elevationProfileDistance(
  point1: Pick<RoutePoint, "ele" | "distance">,
  point2: Pick<RoutePoint, "ele" | "distance">,
  point3: Pick<RoutePoint, "ele" | "distance">,
) {
  if (point1.ele === null || point2.ele === null || point3.ele === null) return 0;

  const x1 = point1.distance;
  const x2 = point2.distance;
  const x3 = point3.distance;
  const y1 = point1.ele;
  const y2 = point2.ele;
  const y3 = point3.ele;
  const dist = Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2);

  if (dist === 0) return Math.sqrt((x3 - x1) ** 2 + (y3 - y1) ** 2);
  return Math.abs((y2 - y1) * x3 - (x2 - x1) * y3 + x2 * y1 - y2 * x1) / dist;
}

function distanceWindowSmoothing(
  points: Pick<RoutePoint, "distance">[],
  left: number,
  right: number,
  window: number,
  compute: (start: number, end: number) => number,
  callback: (value: number, index: number) => void,
) {
  let start = left;
  for (let index = left; index < right; index++) {
    while (start + 1 < index && points[index]!.distance - points[start]!.distance > window) start++;
    let end = Math.min(index + 2, right);
    while (end < right && points[end]!.distance - points[index]!.distance <= window) end++;
    callback(compute(start, end - 1), index);
  }
}

function haversine(lat1: number, lon1: number, lat2: number, lon2: number) {
  const radius = 6371000;
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLon = (lon2 - lon1) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) ** 2;
  return 2 * radius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
